import { Cancel } from '@mui/icons-material';
import { ReportProblem } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import {
  cancelProcess,
  markAsLostProcess,
  startClaimProcess,
} from 'lib/api/sgr';
import { SGR_LOST_REASONS } from 'lib/consts';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CustomButton from 'lib/designSystem/Button';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

type Type = 'cancel' | 'claim' | 'lost';

interface Props {
  open: boolean;
  onClose: () => void;
  type: Type;
  id: number;
  refreshWarranty: () => void;
}

interface Values {
  icon: React.ReactNode;
  color: 'error' | 'warning' | 'info' | 'success';
  title: string;
  message: string;
  description: string;
}

const getValues = (type: Type): Values | null => {
  switch (type) {
    case 'cancel':
      return {
        icon: <Cancel color="error" />,
        color: 'error',
        title: 'Confirmar Anulación',
        message:
          '¿Estás seguro de querer anular esta garantía? Al anularla, el documento asociado ya no estará disponible para el cliente.',
        description: 'Esta acción no se puede deshacer una vez confirmada.',
      };
    case 'claim':
      return {
        icon: <ReportProblem color="warning" />,
        color: 'warning',
        title: 'Confirmar Siniestro',
        message:
          '¿Estás seguro de querer iniciar el proceso de siniestro para esta garantía? Al iniciar el proceso, el documento ya no estará disponible para el cliente y se iniciará un proceso de cobranza por el monto de la garantía.',
        description: 'Esta acción no se puede deshacer una vez confirmada.',
      };
    case 'lost':
      return {
        icon: <Cancel color="error" />,
        color: 'error',
        title: 'Confirmar Oportunidad Perdida',
        message:
          '¿Estás seguro de querer marcar esta oportunidad como perdida?',
        description: 'Esta acción no se puede deshacer una vez confirmada.',
      };
    default:
      return null;
  }
};

const ConfirmActionDialog = ({
  open,
  onClose,
  type,
  id,
  refreshWarranty,
}: Props) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [claimReason, setClaimReason] = useState<string | undefined>();
  const [lostReason, setLostReason] = useState<string | null>(null);
  const [lostDescription, setLostDescription] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState<string | undefined>();
  const { mutateAsync: claim } = startClaimProcess(dispatch);
  const { mutateAsync: markAsLost } = markAsLostProcess(dispatch);
  const { mutateAsync: cancel } = cancelProcess(dispatch);

  const values = getValues(type);
  if (!values) return null;
  const { icon, color, title, message, description } = values;

  const handleClaim = async () => {
    if (!claimReason) {
      showAlert(dispatch, 'error', 'Debes ingresar un motivo de siniestro');
      return;
    }
    await claim({ id, data: { claimReason } });
  };

  const handleCancel = async () => {
    if (!cancelReason) {
      showAlert(dispatch, 'error', 'Debes ingresar un motivo de anulación');
      return;
    }
    await cancel({ id, data: { cancelReason } });
  };

  const handleMarkAsLost = async () => {
    if (!lostReason) {
      showAlert(dispatch, 'error', 'Debes ingresar un motivo de siniestro');
      return;
    }
    await markAsLost({ id, data: { lostReason, lostDescription } });
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      switch (type) {
        case 'claim':
          await handleClaim();
          break;
        case 'cancel':
          await handleCancel();
          break;
        case 'lost':
          await handleMarkAsLost();
          break;
        default:
          showAlert(dispatch, 'error', 'Acción no válida');
      }
      if (refreshWarranty) refreshWarranty();
      showAlert(dispatch, 'success', 'Acción confirmada exitosamente');
    } catch (error) {
      errorHandler(dispatch, 'Problema al confirmar la acción');
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle onClose={onClose} title={title} icon={icon} color={color} />
      <DialogContent>
        {type === 'cancel' && (
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            margin="normal"
            label="Motivo de la anulación"
            placeholder="Describe el motivo de la anulación"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        )}
        {type === 'claim' && (
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            margin="normal"
            label="Motivo del siniestro"
            placeholder="Describe el motivo del siniestro"
            value={claimReason}
            onChange={(e) => setClaimReason(e.target.value)}
          />
        )}
        {type === 'lost' && (
          <Stack direction="column" spacing={2} py={1}>
            <TextField
              select
              fullWidth
              label="Motivo de la pérdida"
              value={lostReason || ''}
              onChange={(e) => setLostReason(e.target.value)}
            >
              {SGR_LOST_REASONS.map((reason) => (
                <MenuItem key={reason.value} value={reason.value}>
                  {reason.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={6}
              margin="normal"
              label="Descripción adicional"
              placeholder="Describe el motivo de la pérdida (opcional)"
              value={lostDescription}
              onChange={(e) => setLostDescription(e.target.value)}
            />
          </Stack>
        )}
        <AlertBanner
          severity={color}
          title={title}
          message={message}
          description={description}
        />
      </DialogContent>
      <DialogActions>
        <CustomButton color="secondary" onClick={onClose}>
          Cancelar
        </CustomButton>
        <CustomButton color={color} onClick={handleConfirm} loading={loading}>
          Confirmar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmActionDialog;
